import { useEffect, useRef, useState } from 'react';
import { Avatar } from '../../../components';
import { Message } from '../../../types';
import { formatTime, cn } from '../../../lib/utils';
import { FormattedMessage } from '../../../lib/formatMessage';
import VoiceNotePlayer from './VoiceNotePlayer';
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  StarIcon,
  FlagIcon,
  ShoppingBagIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, FlagIcon as FlagSolid } from '@heroicons/react/24/solid';
import { useFeedback, useDisputeFeedback } from '../../conversations/hooks/useFeedback';

interface MessageItemProps {
  message: Message;
  onEdit?: (messageId: string, content: string) => Promise<void> | void;
  onDelete?: (messageId: string) => Promise<void> | void;
}

export default function MessageItem({ message, onEdit, onDelete }: MessageItemProps) {
  // CSAT prompts and responses render as centered timeline events, not as
  // chat bubbles — see Phase 4 of docs/customer-feedback.md.
  if (message.metadata?.kind === 'csat_prompt' || message.metadata?.kind === 'csat_response') {
    return <CsatTimelineEvent message={message} />;
  }

  // Outbound utility messages echoed from another app on the shared WABA
  // (currently Shopify order_confirmed / shipped / etc.). Rendered as a
  // discrete notification card so staff can see what the customer received
  // without it looking like staff or the agent sent it.
  if (message.sender === 'system' && message.metadata?.source === 'shopify_utility') {
    return <ShopifyUtilityEvent message={message} />;
  }

  const isCustomer = message.sender === 'customer';
  const isAgent = message.sender === 'agent';
  const isDeleted = !!message.deletedAt;
  const isEdited = !!message.editedAt;
  const canMutate = isAgent && !isDeleted && (onEdit || onDelete);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const [saving, setSaving] = useState(false);

  // If the bubble is updated from elsewhere (socket broadcast) while staff
  // had it open in edit mode, drop their edit-in-progress to avoid clobbering
  // the broadcast. Cheap heuristic — only sync the draft when not editing.
  useEffect(() => {
    if (!isEditing) setDraft(message.content);
  }, [message.content, isEditing]);

  const handleSaveEdit = async () => {
    if (!onEdit) return;
    const trimmed = draft.trim();
    if (!trimmed || trimmed === message.content) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onEdit(message.id, trimmed);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn('group relative flex items-end gap-2', isCustomer ? 'justify-start' : 'justify-end')}
    >
      {isCustomer && (
        <Avatar name={message.senderName || 'Customer'} size="sm" />
      )}

      <div className={cn('flex max-w-[85%] flex-col sm:max-w-[70%]', isCustomer ? 'items-start' : 'items-end')}>
        {message.senderName && !isCustomer && (
          <span className="mb-1 text-xs text-ink-3">{message.senderName}</span>
        )}

        {isDeleted ? (
          <div className="flex items-center gap-1.5 rounded-card border border-dashed border-line bg-surface-2 px-3 py-2 text-xs italic text-ink-3">
            <NoSymbolIcon className="h-3.5 w-3.5 shrink-0" />
            <span>
              AI message removed
              {message.deletedByName ? ` by ${message.deletedByName}` : ''}
            </span>
          </div>
        ) : (
          <div className="relative">
            <div
              className={cn(
                'rounded-card px-4 py-2.5',
                isCustomer && 'bg-surface text-ink border border-line',
                isAgent && 'bg-brand/10 text-ink border border-brand/20',
                !isCustomer && !isAgent && 'bg-brand text-white'
              )}
            >
              {/* Media content */}
              {message.mediaUrl && <MediaContent message={message} isStaff={!isCustomer && !isAgent} />}

              {/* Text content or inline edit */}
              {isEditing ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                    rows={Math.max(2, Math.min(8, draft.split('\n').length))}
                    className="w-full min-w-[240px] resize-y rounded-xl border border-line bg-surface px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
                    disabled={saving}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setDraft(message.content);
                        setIsEditing(false);
                      }}
                      disabled={saving}
                      className="rounded-pill px-2 py-1 text-xs text-ink-2 hover:bg-black/[0.04] disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={saving || !draft.trim()}
                      className="rounded-pill bg-brand px-2 py-1 text-xs font-medium text-white hover:bg-brand/90 disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                message.content && !(message.mediaUrl && /^\[(Audio|Image|Video)\]$/.test(message.content)) && (
                  <div className="whitespace-pre-wrap break-words text-sm">
                    <FormattedMessage text={message.content} />
                  </div>
                )
              )}
            </div>

            {/* Hover-revealed action menu for AI messages */}
            {canMutate && !isEditing && (
              <MessageActionsMenu
                onEdit={onEdit ? () => setIsEditing(true) : undefined}
                onDelete={onDelete ? () => onDelete(message.id) : undefined}
              />
            )}
          </div>
        )}

        <span className="mt-1 text-xs text-ink-3">
          {message.createdAt ? formatTime(message.createdAt) : ''}
          {isEdited && !isDeleted && (
            <span
              className="ml-1 italic text-ink-3"
              title={
                message.editedByName
                  ? `Edited by ${message.editedByName}`
                  : 'Edited by staff'
              }
            >
              (edited)
            </span>
          )}
        </span>
      </div>

      {!isCustomer && message.senderName && (
        <Avatar name={message.senderName} size="sm" />
      )}
    </div>
  );
}

function MessageActionsMenu({
  onEdit,
  onDelete,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="absolute -left-7 top-1/2 -translate-y-1/2">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'rounded-full p-1 text-ink-3 transition-opacity hover:bg-black/[0.04] hover:text-ink',
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
        aria-label="Message actions"
      >
        <EllipsisVerticalIcon className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-10 mt-1 w-40 overflow-hidden rounded-card border border-line bg-surface shadow-m">
          {onEdit && (
            <button
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ink hover:bg-black/[0.04]"
            >
              <PencilSquareIcon className="h-4 w-4 shrink-0" />
              Edit message
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-danger hover:bg-danger/10"
            >
              <TrashIcon className="h-4 w-4 shrink-0" />
              Withdraw
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MediaContent({ message, isStaff }: { message: Message; isStaff: boolean }) {
  const mediaType = message.mediaType;
  const url = message.mediaUrl!;

  if (mediaType === 'image') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="mb-1 block">
        <img
          src={url}
          alt="Shared image"
          className="max-h-64 max-w-full rounded-lg object-contain"
          loading="lazy"
        />
      </a>
    );
  }

  if (mediaType === 'video') {
    return (
      <video
        src={url}
        controls
        className="mb-1 max-h-64 max-w-full rounded-lg"
        preload="metadata"
      />
    );
  }

  if (mediaType === 'audio') {
    return (
      <VoiceNotePlayer
        src={url}
        transcript={message.metadata?.audio_transcript}
      />
    );
  }

  // Document / fallback
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'mb-1 flex items-center gap-2 rounded-lg border p-2 text-sm transition-colors',
        isStaff
          ? 'border-white/30 text-white hover:bg-white/10'
          : 'border-line text-ink-2 hover:bg-black/[0.04]'
      )}
    >
      <DocumentIcon className="h-5 w-5 shrink-0" />
      <span className="truncate">{message.fileName || getFilename(url)}</span>
      <ArrowDownTrayIcon className="h-4 w-4 shrink-0" />
    </a>
  );
}

function getFilename(url: string): string {
  try {
    const path = url.includes('://') ? new URL(url).pathname : url;
    return path.split('/').pop() || 'Document';
  } catch {
    return 'Document';
  }
}

function ShopifyUtilityEvent({ message }: { message: Message }) {
  const templateName = message.metadata?.template_name as string | undefined;
  const ts = message.createdAt ? formatTime(message.createdAt) : '';

  // The backend renders the template into a multi-line body. Hide the first
  // header line (`[Automated WhatsApp template sent to customer: ...]`) since
  // we already surface the template name + a Shopify label visually.
  const body = (message.content || '')
    .split('\n')
    .filter((line) => line.trim() && !line.startsWith('[Automated WhatsApp template'))
    .join('\n');

  const prettyName = templateName
    ? templateName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Utility message';

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[85%] rounded-card border border-line bg-surface-2 px-3 py-2 text-xs text-ink-2 sm:max-w-[70%]">
        <div className="mb-1 flex items-center gap-1.5 text-ink-2">
          <ShoppingBagIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="font-medium">Shopify · {prettyName}</span>
          <span className="text-ink-3">· {ts}</span>
        </div>
        {body && (
          <pre className="whitespace-pre-wrap break-words font-sans text-ink-2">
            {body}
          </pre>
        )}
      </div>
    </div>
  );
}

function CsatTimelineEvent({ message }: { message: Message }) {
  const isPrompt = message.metadata?.kind === 'csat_prompt';
  const isResponse = message.metadata?.kind === 'csat_response';
  const score = message.metadata?.score ?? 0;
  const feedbackId = message.metadata?.feedback_id;
  const ts = message.createdAt ? formatTime(message.createdAt) : '';

  // Pull the live feedback row to know `disputed` state. Reads from the
  // same useFeedback cache ChatHeader uses, so no extra network request.
  const { feedback } = useFeedback(message.conversationId);
  const row = feedbackId ? feedback.find((r) => r.id === feedbackId) : undefined;
  const disputed = row?.disputed ?? false;

  const dispute = useDisputeFeedback();

  const handleToggleDispute = () => {
    if (!feedbackId) return;
    const next = !disputed;
    const verb = next ? 'flag this rating as disputed' : 'remove the dispute flag';
    if (!window.confirm(`Are you sure you want to ${verb}?`)) return;
    dispute.mutate({ feedbackId, disputed: next });
  };

  return (
    <div className="flex justify-center">
      <div
        className={cn(
          'inline-flex max-w-[85%] items-center gap-2 rounded-full border px-3 py-1.5 text-xs',
          isPrompt && 'border-line bg-surface-2 text-ink-2',
          isResponse && !disputed && 'border-warning/50 bg-warning/10 text-warning',
          isResponse && disputed && 'border-line bg-surface-2 text-ink-3'
        )}
      >
        {isPrompt && (
          <>
            <StarIcon className="h-3.5 w-3.5 shrink-0" />
            <span>Feedback survey sent · {ts}</span>
          </>
        )}
        {isResponse && (
          <>
            <span
              className={cn('flex items-center gap-0.5', disputed && 'opacity-50 line-through')}
              aria-label={`Rated ${score} out of 5`}
            >
              {[1, 2, 3, 4, 5].map((n) =>
                n <= score ? (
                  <StarSolid
                    key={n}
                    className={cn('h-3.5 w-3.5', disputed ? 'text-ink-3' : 'text-warning')}
                  />
                ) : (
                  <StarIcon
                    key={n}
                    className={cn(
                      'h-3.5 w-3.5',
                      disputed ? 'text-ink-3' : 'text-warning/40'
                    )}
                  />
                )
              )}
            </span>
            <span className={cn('font-medium', disputed && 'line-through')}>
              Customer rated {score}/5
            </span>
            {disputed && <span className="font-semibold uppercase tracking-wider">Disputed</span>}
            <span className={disputed ? 'text-ink-3' : 'text-warning/70'}>· {ts}</span>
            {feedbackId && (
              <button
                onClick={handleToggleDispute}
                disabled={dispute.isPending}
                className={cn(
                  'ml-1 rounded-full p-0.5 transition-colors disabled:opacity-50',
                  disputed
                    ? 'text-ink-3 hover:bg-black/[0.04] hover:text-ink'
                    : 'text-warning/70 hover:bg-black/[0.04] hover:text-warning'
                )}
                title={disputed ? 'Remove dispute flag' : 'Flag this rating as disputed'}
                aria-label={disputed ? 'Remove dispute flag' : 'Flag this rating as disputed'}
              >
                {disputed ? (
                  <FlagSolid className="h-3 w-3" />
                ) : (
                  <FlagIcon className="h-3 w-3" />
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
